import _ from 'lodash'
import { useState } from 'react'
import { flushSync } from 'react-dom'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { useRerenderOnMutation_deprecated } from '../../hooks/useRerenderOnMutation'
import { CacheFactory } from '../../models/CacheFactory'
import { Edition } from '../../models/Edition'
import { Feature } from '../../models/Feature'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Switch } from '../common/fields/Switch'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { HandleUpgradeOpts, SelectPlanPage } from './SelectPlanPage'
import { TutorialVideoModal } from './TutorialVideoModal'

type SetupFeaturesPageData = {
    user: User
    record: User
    featureData: Feature[]
    topFeatureData: Feature[]
    lockedFeatureData: Feature[]
}

type SettingsFeatureProps = {
    feature: Feature
    isLocked: boolean
    record: User
    playTutorial: (feature: Feature) => void
    featureChanged: (feature: Feature, isEnabled: boolean) => void
}

const SettingsFeature = ({
    feature,
    isLocked,
    record,
    playTutorial,
    featureChanged,
}: SettingsFeatureProps) => {
    return (
        <WorkflowPage.IconSection
            key={feature.id}
            title={feature.name ?? ''}
            icon={feature.iconStringForOwner(record)}
            description={feature.memo}
            hasHoverEffect={true}
            thirdLine={
                <a
                    className="link text-primary-700 hover:text-primary-700/60 text-sm underline"
                    onClick={(e: { preventDefault: () => void }) => {
                        e.preventDefault()
                        playTutorial(feature)
                    }}
                >
                    Learn more
                </a>
            }
            secondaryView={
                <Switch
                    isOn={Feature.enabled(feature.feature, record)}
                    onChange={(isEnabled) => featureChanged(feature, isEnabled)}
                />
            }
            infoTag={isLocked ? 'Upgrade' : undefined}
        ></WorkflowPage.IconSection>
    )
}

const viewWillAppear = async (base: PocketComponent) => {
    const data: SetupFeaturesPageData = {
        user: base.me,
        record: base.me.clone(),
        featureData: [],
        topFeatureData: [],
        lockedFeatureData: [],
    }

    const features = await RecordFactory.getInstance().loadFeatures()
    for (const feature of features) {
        if (['booking', 'invoice', 'payment'].includes(feature.id!)) {
            continue
        }
        if (feature.allowed(data.record.industry!)) {
            if (data.record.industry!.defaultOn(feature)) {
                data.topFeatureData.push(feature)
            }
            else if (feature.requiresUpgrade(data.record)) {
                data.lockedFeatureData.push(feature)
            }
            else {
                data.featureData.push(feature)
            }
        }
    }
    return data
}

const render = ({ data, base }: PocketComponentProps<SetupFeaturesPageData>) => {
    const navigate = usePSNavigate()
    const record = useRerenderOnMutation_deprecated(data.record)
    const features = [...data.topFeatureData, ...data.featureData, ...data.lockedFeatureData]

    const [changeCount, setChangeCount] = useState(0)
    const [dependentFeatures, setDependentFeatures] = useState<Feature[]>([])
    const [selectedFeature, setSelectedFeature] = useState<Feature | undefined>(undefined)
    const [currentModalName, setCurrentModalName] = useState<
        'video_modal' | 'unlock_modal' | 'unlock_flow' | 'warning_modal' | undefined
    >(undefined)
    const [isSaving, setIsSaving] = useState(false)

    function checkPartnerFeature(feature: Feature, user: User, edition: Edition | undefined) {
        if (edition?.allowBasicPlan) {
            const freeFeatures = edition.basicFeatures
            const premiumFeatures = edition.features
            if (!user.plan) {
                return freeFeatures.includes(feature.id!)
            }
            else {
                return freeFeatures.includes(feature.id!) || premiumFeatures.includes(feature.id!)
            }
        }
        return false
    }

    const getFeatureKey = (feature: Feature): string => {
        // Special case for feature key with wrong case in db
        if (feature.key === 'allowSalestax') {
            return 'allowSalesTax'
        }
        else {
            return feature.key
        }
    }

    const onChange = (feature: Feature, isEnabled: boolean) => {
        if (feature.allowed(record.industry!)) {
            const featureKey = getFeatureKey(feature)
            ;(record as any)[featureKey] = isEnabled
            if ((record as any)[featureKey] !== (data.user as any)[featureKey]) {
                setChangeCount((prev) => prev + 1)
            }
            else {
                setChangeCount((prev) => prev - 1)
            }
        }
    }

    function closeModal() {
        setSelectedFeature(undefined)
        setCurrentModalName(undefined)
    }

    function playTutorial(feature: Feature) {
        setSelectedFeature(feature)
        setCurrentModalName('video_modal')
    }

    function displayUnlockModal() {
        setCurrentModalName('unlock_modal')
    }

    function displayUnlockFlow() {
        setCurrentModalName('unlock_flow')
    }

    function displayWarningModal() {
        setCurrentModalName('warning_modal')
    }
    function toggleOffDependentFeatures() {
        for (const f of dependentFeatures) {
            onChange(f, false)
        }
        onChange(selectedFeature!, false)
        closeModal()
    }

    function handleUpgrade(opts?: HandleUpgradeOpts) {
        if (opts?.cancel) {
            closeModal()
        }
    }

    function featureChanged(feature: Feature, isChecked: boolean) {
        setSelectedFeature(feature)
        const parent = CacheFactory.getInstance().getRecord(Feature, feature.parent) as Feature
        if (isChecked) {
            if (
                feature.requiresUpgrade(record) &&
                !checkPartnerFeature(feature, record, record.edition!)
            ) {
                displayUnlockModal()
            }
            else if (parent && !Feature.enabled(parent.feature, record)) {
                onChange(parent, true)
                onChange(feature, true)
            }
            else {
                onChange(feature, true)
            }
        }
        else {
            const featureList = []
            for (const f of features) {
                if (f.parent === feature.id && Feature.enabled(f.feature, record)) {
                    featureList.push(f)
                }
            }
            setDependentFeatures(featureList)
            if (featureList.length) {
                displayWarningModal()
            }
            else {
                onChange(feature, false)
            }
        }
    }

    const submit = async () => {
        flushSync(() => {
            setIsSaving(true)
        })

        base.showProgressBar('Updating account..')

        const result = await RecordFactory.getInstance().submitFeatures(record)

        base.hideProgressBar()
        setIsSaving(false)

        if (result.ok) {
            navigate('/settings')
        }
        else {
            base.logAndDisplayError(result.error)
        }
    }

    return (
        <WorkflowPage
            title="Features"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" disabled={changeCount === 0} onClick={submit}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="Recommended for you">
                    {data.topFeatureData
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((feature) => (
                            <SettingsFeature
                                key={feature.id}
                                feature={feature}
                                record={record}
                                isLocked={false}
                                playTutorial={playTutorial}
                                featureChanged={featureChanged}
                            />
                        ))}
                </WorkflowPage.Card>
                <WorkflowPage.Card title="Available in your plan">
                    {data.featureData
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((feature) => (
                            <SettingsFeature
                                key={feature.id}
                                feature={feature}
                                record={record}
                                isLocked={false}
                                playTutorial={playTutorial}
                                featureChanged={featureChanged}
                            />
                        ))}
                    {data.lockedFeatureData
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((feature) => (
                            <SettingsFeature
                                key={feature.id}
                                feature={feature}
                                record={record}
                                isLocked={true}
                                playTutorial={playTutorial}
                                featureChanged={featureChanged}
                            />
                        ))}
                </WorkflowPage.Card>
            </div>
            <NavigationBlockerPrompt when={!isSaving && changeCount > 0} />

            <Modal
                onClose={closeModal}
                isOpen={currentModalName === 'unlock_modal'}
                size="small"
                title={
                    selectedFeature?.requiredPlan === 'plus'
                        ? 'Team feature'
                        : selectedFeature?.requiredPlan === 'suite'
                          ? 'Enterprise feature'
                          : 'Premium feature'
                }
                showDividers={false}
                footer={
                    <Button intent="primary" onClick={displayUnlockFlow}>
                        Unlock Feature
                    </Button>
                }
            >
                {selectedFeature?.pitch}
            </Modal>

            {currentModalName === 'video_modal' && (
                <TutorialVideoModal onClose={closeModal} feature={selectedFeature} />
            )}

            <ConfirmationModal
                isOpen={currentModalName === 'warning_modal' && selectedFeature?.name !== undefined}
                title={`Disable ${selectedFeature?.name}?`}
                onClose={closeModal}
                onConfirm={toggleOffDependentFeatures}
                shouldHideCancelButton
                confirmButtonText="Yes, disable"
            >
                {`Disabling ${selectedFeature?.name} will also disable: ${dependentFeatures.map((c) => c.name).join(', ')}`}
            </ConfirmationModal>

            {currentModalName === 'unlock_flow' && (
                <SelectPlanPage
                    context="feature"
                    onNext={() => navigate('/')}
                    handleUpgrade={handleUpgrade}
                    planId={selectedFeature!.requiredPlan}
                />
            )}
        </WorkflowPage>
    )
}

export const SetupFeaturesPage = usePSComponent(render, viewWillAppear)
